/**
 * DDTDaLista - Importazione DDT da Google Sheets
 * 
 * Permette all'utente di:
 * 1. Creare uno spreadsheet template per inserimento DDT
 * 2. Elaborare uno spreadsheet esistente per salvare i DDT
 */
import React, { useState, useCallback } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { useCreaSpreadsheetDdtLista, useProcessaSpreadsheetDdtLista } from './queries';

// Intestazioni colonne del template
const COLONNE_TEMPLATE = [
    'cod_bolla', 'num_bolla', 'data_bolla', 'rag_soc', 'targa',
    'rag_soc_vettore', 'vett_ns', 'rag_soc_conducente', 'cod_commessa', 'data_inizio',
    'descrizion', 'destinazione', 'destinazione_pattern', 'unita', 'lordo',
    'tara', 'pesospec', 'num_silos', 'temperatura', 'cig', 'cup',
    'stampante', 'stamparescontrino', 'esito', 'num_bolla_assegnato', 'data_inserimento'
];

export function DDTDaLista() {
    // Stato per creazione template
    const [createdUrl, setCreatedUrl] = useState(null);
    const { creaSpreadsheet, loading: loadingCrea, error: errorCrea } = useCreaSpreadsheetDdtLista();

    // Stato per elaborazione
    const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
    const [nomeSheet, setNomeSheet] = useState('DDTLISTA');
    const [rigaIntestazione, setRigaIntestazione] = useState(2);
    const [elaborazioneResult, setElaborazioneResult] = useState(null);
    const { processaSpreadsheet, loading: loadingProcessa, error: errorProcessa } = useProcessaSpreadsheetDdtLista();

    // Crea nuovo spreadsheet template
    const handleCreaTemplate = useCallback(async () => {
        try {
            const result = await creaSpreadsheet();
            if (result?.url) {
                setCreatedUrl(result.url);
            }
        } catch (err) {
            console.error('Errore creazione template:', err);
        }
    }, [creaSpreadsheet]);

    // Elabora spreadsheet esistente
    const handleElabora = useCallback(async () => {
        if (!spreadsheetUrl.trim()) return;
        try {
            setElaborazioneResult(null);
            const result = await processaSpreadsheet(
                spreadsheetUrl.trim(),
                nomeSheet || 'DDTLISTA',
                rigaIntestazione || 2
            );
            setElaborazioneResult(result);
        } catch (err) {
            console.error('Errore elaborazione:', err);
        }
    }, [spreadsheetUrl, nomeSheet, rigaIntestazione, processaSpreadsheet]);

    // Estrai spreadsheet ID da URL
    const extractSpreadsheetId = (url) => {
        const match = url.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
        return match ? match[1] : null;
    };

    const isValidUrl = spreadsheetUrl.trim() && extractSpreadsheetId(spreadsheetUrl);

    return (
        <div className="p-3">
            <h2>Importazione DDT da Google Sheets</h2>
            
            {/* Sezione 1: Crea Template */}
            <Card title="1. Crea Spreadsheet Template" className="mb-4">
                <p className="text-muted">
                    Crea un nuovo file Google Sheets nel tuo Drive con le intestazioni 
                    predisposte per l'inserimento dei DDT.
                </p>
                
                <Button 
                    label="Crea Template DDT" 
                    icon="pi pi-file-excel"
                    onClick={handleCreaTemplate}
                    disabled={loadingCrea}
                    className="p-button-success"
                />
                
                {loadingCrea && (
                    <span className="ml-3">
                        <ProgressSpinner style={{width: '20px', height: '20px'}} strokeWidth="4" />
                        <span className="ml-2">Creazione in corso...</span>
                    </span>
                )}

                {errorCrea && (
                    <Message severity="error" text={errorCrea.message} className="mt-3 w-100" />
                )}

                {createdUrl && (
                    <div className="mt-3">
                        <Message severity="success" text="Template creato!" className="mb-2" />
                        <div>
                            <a href={createdUrl} target="_blank" rel="noopener noreferrer" className="p-button p-button-link">
                                <i className="pi pi-external-link mr-2"></i>
                                Apri Spreadsheet
                            </a>
                        </div>
                    </div>
                )}

                <Divider />
                
                <details>
                    <summary className="cursor-pointer text-primary">Colonne del template</summary>
                    <div className="mt-2 p-2 bg-light rounded">
                        <small>
                            {COLONNE_TEMPLATE.slice(0, 23).map((col, i) => (
                                <span key={col} className={i < 23 ? 'text-dark' : 'text-muted'}>
                                    {col}{i < 22 ? ', ' : ''}
                                </span>
                            ))}
                            <br />
                            <em className="text-muted">Output: esito, num_bolla_assegnato, data_inserimento</em>
                        </small>
                    </div>
                </details>
            </Card>

            {/* Sezione 2: Elabora Spreadsheet */}
            <Card title="2. Elabora Spreadsheet" className="mb-4">
                <p className="text-muted">
                    Inserisci il link di uno spreadsheet compilato per elaborare e salvare i DDT.
                    Le colonne esito, num_bolla_assegnato e data_inserimento verranno compilate automaticamente.
                </p>

                <div className="p-inputgroup mb-3">
                    <span className="p-inputgroup-addon">
                        <i className="pi pi-link"></i>
                    </span>
                    <InputText 
                        value={spreadsheetUrl}
                        onChange={(e) => setSpreadsheetUrl(e.target.value)}
                        placeholder="https://docs.google.com/spreadsheets/d/..."
                        className="w-100"
                    />
                </div>

                <div className="p-grid mb-3">
                    <div className="p-col-12 p-md-6">
                        <div className="field">
                            <label htmlFor="nomeSheet" className="block mb-2">Nome foglio (default: DDTLISTA)</label>
                            <InputText 
                                id="nomeSheet"
                                value={nomeSheet}
                                onChange={(e) => setNomeSheet(e.target.value)}
                                placeholder="DDTLISTA"
                                className="w-100"
                            />
                        </div>
                    </div>
                    <div className="p-col-12 p-md-6">
                        <div className="field">
                            <label htmlFor="rigaIntestazione" className="block mb-2">Riga intestazione (default: 2)</label>
                            <InputText 
                                id="rigaIntestazione"
                                value={rigaIntestazione}
                                onChange={(e) => setRigaIntestazione(parseInt(e.target.value) || 2)}
                                type="number"
                                min="1"
                                className="w-100"
                            />
                        </div>
                    </div>
                </div>

                <Button 
                    label="Elabora"
                    icon="pi pi-play"
                    onClick={handleElabora}
                    disabled={loadingProcessa || !isValidUrl}
                    className="p-button-primary"
                />

                {!isValidUrl && spreadsheetUrl.trim() && (
                    <small className="p-error">URL non valido. Inserire un link Google Sheets.</small>
                )}

                {loadingProcessa && (
                    <div className="mt-3 text-center">
                        <ProgressSpinner style={{width: '30px', height: '30px'}} strokeWidth="4" />
                        <p>Elaborazione DDT in corso...</p>
                    </div>
                )}

                {errorProcessa && (
                    <Message severity="error" text={errorProcessa.message} className="mt-3 w-100" />
                )}

                {elaborazioneResult && (
                    <div className="mt-3">
                        <Message 
                            severity={elaborazioneResult.errori > 0 ? 'warn' : 'success'} 
                            className="w-100"
                        >
                            <div>
                                <strong>Elaborazione completata</strong>
                                <ul className="mb-0 mt-2">
                                    <li>Righe elaborate: {elaborazioneResult.elaborate}</li>
                                    <li>DDT salvati: {elaborazioneResult.salvati}</li>
                                    {elaborazioneResult.errori > 0 && (
                                        <li className="text-danger">Errori: {elaborazioneResult.errori}</li>
                                    )}
                                    {elaborazioneResult.saltate > 0 && (
                                        <li>Righe saltate (già elaborate): {elaborazioneResult.saltate}</li>
                                    )}
                                </ul>
                            </div>
                        </Message>
                        <div className="mt-2">
                            <a href={spreadsheetUrl} target="_blank" rel="noopener noreferrer">
                                <i className="pi pi-external-link mr-2"></i>
                                Apri Spreadsheet per vedere gli esiti
                            </a>
                        </div>
                    </div>
                )}
            </Card>

            {/* Note */}
            <Card className="bg-light">
                <h6><i className="pi pi-info-circle mr-2"></i>Note</h6>
                <ul className="mb-0">
                    <li>Il foglio di defaulto di chiama <strong>DDTLISTA</strong></li>
                    <li>La riga 1 contiene i titoli leggibili, la riga 2 i nomi-campo (intestazione tecnica)</li>
                    <li>Per default l'applicazione legge l'intestazione tecnica dalla riga 2</li>
                    <li>Le righe con esito già valorizzato vengono saltate</li>
                </ul>
            </Card>
        </div>
    );
}

export default DDTDaLista;
